import type { FormSelectOption } from '@practium/ui';
import {
	FERTILIZER_PACK_SLOTS,
	PRODUCT_PRICE_UNIT_OPTIONS,
} from '../constants/maintenance.constants';
import type { MaintenanceProductPrice } from '../types';

export type ProductPriceMap = Record<string, MaintenanceProductPrice>;

export interface PackProduct {
	id: string;
	name: string;
}

export interface ProductCostFormEntry {
	price: string;
	unit: string;
}

const inputFormatter = new Intl.NumberFormat('pt-BR', {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
	useGrouping: false,
});

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export const productKey = (id: string) => id.toLowerCase();

export const toPriceUnitValue = (price: Pick<MaintenanceProductPrice, 'unit' | 'bagWeightKg'>) =>
	price.unit === 'bag' ? `bag:${price.bagWeightKg ?? ''}` : price.unit;

export const fromPriceUnitValue = (
	value: string
): Pick<MaintenanceProductPrice, 'unit' | 'bagWeightKg'> => {
	if (value.startsWith('bag:')) return { unit: 'bag', bagWeightKg: Number(value.slice(4)) };
	return { unit: value === 'ton' ? 'ton' : 'kg' };
};

export const priceUnitLabel = (value: string) => {
	const option = PRODUCT_PRICE_UNIT_OPTIONS.find((item) => item.value === value);
	if (option) return option.label;
	const { unit, bagWeightKg } = fromPriceUnitValue(value);
	return unit === 'bag' ? `saco ${bagWeightKg} kg` : unit;
};

export const priceUnitOptions = (value: string): FormSelectOption[] =>
	PRODUCT_PRICE_UNIT_OPTIONS.some((option) => option.value === value)
		? PRODUCT_PRICE_UNIT_OPTIONS
		: [{ value, label: priceUnitLabel(value) }, ...PRODUCT_PRICE_UNIT_OPTIONS];

export const parsePriceInput = (value: string): number | null => {
	const normalized = value.replace(/[R$\s.]/g, '').replace(',', '.');
	if (!normalized) return null;
	const parsed = Number(normalized);
	return Number.isFinite(parsed) && parsed >= 0 ? parsed : Number.NaN;
};

export const formatPriceInput = (value?: number) =>
	value == null || !Number.isFinite(value) ? '' : inputFormatter.format(value);

export const formatPrice = (value: number) => `R$ ${inputFormatter.format(value)}`;

export const formatCurrency = (value: number) => currencyFormatter.format(value);

export const getPackProducts = (
	pack: Partial<Record<(typeof FERTILIZER_PACK_SLOTS)[number]['key'], string | null>> | null | undefined,
	fertilizers: Array<{ _id: string; name?: string }>
): PackProduct[] => {
	if (!pack) return [];
	const ids = FERTILIZER_PACK_SLOTS.map((slot) => pack[slot.key]).filter(
		(id, index, list): id is string => !!id && list.indexOf(id) === index
	);
	return ids.map((id) => ({
		id,
		name: fertilizers.find((fertilizer) => fertilizer._id === id)?.name ?? 'Produto',
	}));
};

export const toProductPriceMap = (prices: MaintenanceProductPrice[] = []): ProductPriceMap =>
	Object.fromEntries(
		prices.map((price) => [
			productKey(price.id),
			{
				id: price.id,
				price: price.price,
				unit: price.unit,
				...(price.unit === 'bag' ? { bagWeightKg: price.bagWeightKg } : {}),
			},
		])
	);

export const isPriceInformed = (price?: MaintenanceProductPrice) =>
	!!price && Number.isFinite(price.price) && price.price > 0;
